// Where each game's player rules come from, for the site's rules pages (/games/:id).
// A game's RULES.md (written for players, versioned with the game) wins; otherwise the page shows
// the game's own describe() text, one section per ruleset in the registry. Both live next to the
// game's code, so the site never keeps a separate copy of the rules.

import type { GameDef } from './registry';
import * as classicCards from './classic-cards/classic-cards';
import * as lodden from './lodden-thinks/lodden';
import * as baduk from './baduk-analysis/baduk';
import * as heistCity from './heist-city/heist-city';
import * as diceFactory from './dice-factory/dice-factory';
import * as henhur from './henhur/henhur';
import * as ktd from './kill-team-draft/kill-team-draft';
import * as ctf from './capture-the-flag/capture-the-flag';
import * as stumble from './stumble-home/stumble-home';
import * as vanLife from './van-life/van-life';
import { DEFAULT_CONFIG as VAN_LIFE_DEFAULTS } from './van-life/types';
import ctfRulesUrl from './capture-the-flag/RULES.md';

type Describe = (config: any) => string;   // eslint-disable-line @typescript-eslint/no-explicit-any

const DESCRIBE: Record<string, { describe: Describe; defaults: object }> = {
  'classic-cards': { describe: classicCards.describe, defaults: classicCards.DEFAULT_CONFIG },
  'lodden-thinks': { describe: lodden.describe, defaults: lodden.DEFAULT_CONFIG },
  'baduk-analysis': { describe: baduk.describe, defaults: baduk.DEFAULT_CONFIG },
  'heist-city': { describe: heistCity.describe, defaults: heistCity.DEFAULT_CONFIG },
  'dice-factory': { describe: diceFactory.describe, defaults: diceFactory.DEFAULT_CONFIG },
  henhur: { describe: henhur.describe, defaults: henhur.DEFAULT_CONFIG },
  'kill-team-draft': { describe: ktd.describe, defaults: ktd.DEFAULT_CONFIG },
  'capture-the-flag': { describe: ctf.describe, defaults: ctf.DEFAULT_CONFIG },
  'stumble-home': { describe: stumble.describe, defaults: stumble.DEFAULT_CONFIG },
  'van-life': { describe: vanLife.describe, defaults: VAN_LIFE_DEFAULTS },
};

// Hand-written player rules (the built file's URL; the page fetches it).
export const RULES_FILES: Record<string, string> = {
  'capture-the-flag': ctfRulesUrl,
};

// The describe() text for each of the game's rulesets (or once, with its defaults, when it has none).
export function describedRules(game: GameDef): { title: string; text: string }[] {
  const d = DESCRIBE[game.id];
  if (!d) return [];
  const sets = game.rulesets.length ? game.rulesets.map(r => ({ title: r.name, config: { ...d.defaults, ...r.config } })) : [{ title: game.name, config: d.defaults }];
  const out: { title: string; text: string }[] = [];
  for (const x of sets) {
    let text = '';
    try { text = d.describe(x.config); } catch { /* a ruleset the text can't describe: left out */ }
    // describe() often opens with its own title line ("War — Regular"): the page has headings.
    const lines = text.split('\n');
    if (lines.length > 1 && lines[0].length < 60 && !/[.:]$/.test(lines[0].trim())) text = lines.slice(1).join('\n').trim();
    // Rulesets that only change a setting the text doesn't mention (a bot's strength) read the same.
    if (text && !out.some(o => o.text === text)) out.push({ title: x.title, text });
  }
  return out;
}
